"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PERMISSION_TEMPLATES = exports.PERMISSION_GROUPS = exports.RESTRICTED_KEY_BY_GROUP = exports.STORABLE_PERMISSION_KEYS = exports.MODULE_RESTRICTED_KEYS = exports.PERMISSION_MODULES = exports.PERMISSION_KEYS = void 0;
exports.moduleOfRestrictedKey = moduleOfRestrictedKey;
/**
 * Dynamic per-user permission vocabulary — shared by the backend (zod
 * validation + enforcement) and the frontend (gating + grants UI) so the
 * two can never drift.
 *
 * Semantics: every user has VIEW access to the orgs they belong to; these
 * keys gate WRITES. Grants attach to a specific User row (organization-user
 * scope) via the UserPermission table. Org admins (`User.isAdmin`)
 * implicitly hold all keys; while the permission kill switch is off
 * (permissionGates.ts) every session holds all keys.
 *
 * Keys are stored as plain strings — adding a key here needs no migration;
 * removed keys are filtered out at read time.
 */
exports.PERMISSION_KEYS = [
    "batch.create",
    "batch.edit",
    "batch.delete",
    "batch.launch",
    "monitoring.edit",
    "defaults.create",
    "defaults.edit",
    "defaults.delete",
    "members.invite",
    "members.edit",
    "members.remove",
    "members.roles",
    "members.assignClients",
    "clients.manage",
    "slackBots.manage",
    "creators.invite",
    "creators.sendLink",
    "creators.message",
    "creators.edit",
    "creators.photo",
    "creators.remove",
    "creators.metaPermission",
    "creators.tagging",
    "creators.payments",
];
/** The modules whose visibility can be revoked ("No access"). */
exports.PERMISSION_MODULES = [
    "batches",
    "batchDetails",
    "monitoring",
    "defaults",
    "members",
    "clients",
    "slackBots",
    "creators",
];
/**
 * Module restriction markers: stored through the same grants table, they
 * mean "this member should NOT see this module at all" (overriding the
 * default universal view access). They are INVERSE keys and deliberately
 * NOT part of PERMISSION_KEYS — consumers must never check them through
 * `can()`; use `canViewModule(module)` (frontend) / the backend's
 * restricted-modules set, which hide the inverted semantics.
 */
exports.MODULE_RESTRICTED_KEYS = [
    "batches.restricted",
    "batchDetails.restricted",
    "monitoring.restricted",
    "defaults.restricted",
    "members.restricted",
    "clients.restricted",
    "slackBots.restricted",
    "creators.restricted",
];
/** Everything the grants table may store: write grants + module markers. */
exports.STORABLE_PERMISSION_KEYS = [
    ...exports.PERMISSION_KEYS,
    ...exports.MODULE_RESTRICTED_KEYS,
];
/** The restriction marker for each PERMISSION_GROUPS id / module. */
exports.RESTRICTED_KEY_BY_GROUP = {
    batches: "batches.restricted",
    batchDetails: "batchDetails.restricted",
    monitoring: "monitoring.restricted",
    defaults: "defaults.restricted",
    members: "members.restricted",
    clients: "clients.restricted",
    slackBots: "slackBots.restricted",
    creators: "creators.restricted",
};
/** Module a restriction marker belongs to ("batches.restricted" → "batches"). */
function moduleOfRestrictedKey(key) {
    return key.slice(0, key.indexOf("."));
}
/** Ordered grouping for the grants UI (members-page drawer). */
exports.PERMISSION_GROUPS = [
    {
        id: "batches",
        label: "Creative testing batches",
        permissions: [
            {
                key: "batch.create",
                label: "Create batches",
                description: "Create drafts, plan batches, and clone existing ones — everything up to the point of launching.",
            },
            {
                key: "batch.edit",
                label: "Edit batches",
                description: "Rename batches, edit strategic context, and regenerate 7-day reports.",
            },
            {
                key: "batch.delete",
                label: "Delete batches",
                description: "Delete batches, including from the platform.",
            },
            {
                key: "batch.launch",
                label: "Launch and retry",
                description: "Launch batches to the ad platforms (spends budget), relaunch failed ones, deploy scheduled ones, and scale winning ads into scale campaigns.",
            },
        ],
    },
    {
        // View-only module: no write keys — the drawer offers just
        // No access / View. Hierarchical with "batches": restricting the
        // batch LIST also hides detail pages; this key additionally hides
        // detail pages while the list stays visible.
        id: "batchDetails",
        label: "Batch details page",
        permissions: [],
    },
    {
        id: "monitoring",
        label: "Batch monitoring",
        permissions: [
            {
                key: "monitoring.edit",
                label: "Edit monitoring settings",
                description: "Change targets, aggressiveness, and modes on live batches, restart monitoring, and pause a batch's ad set.",
            },
        ],
    },
    {
        id: "defaults",
        label: "Campaign defaults",
        permissions: [
            {
                key: "defaults.create",
                label: "Create defaults",
                description: "Create new default configurations, including from platform targets.",
            },
            {
                key: "defaults.edit",
                label: "Edit defaults",
                description: "Change existing defaults and set the active configuration.",
            },
            {
                key: "defaults.delete",
                label: "Delete defaults",
                description: "Remove default configurations.",
            },
        ],
    },
    {
        id: "members",
        label: "Members",
        permissions: [
            {
                key: "members.invite",
                label: "Invite members",
                description: "Invite new members by email, including bulk invites.",
            },
            {
                key: "members.edit",
                label: "Edit members",
                description: "Edit other members' details and Slack links.",
            },
            {
                key: "members.remove",
                label: "Remove members",
                description: "Remove other members from the organization.",
            },
            {
                key: "members.roles",
                label: "Change roles",
                description: "Promote members to admin or demote them. Admin changes additionally require being an org admin yourself — a grant alone is never enough to mint an admin.",
            },
            {
                key: "members.assignClients",
                label: "Manage client access",
                description: "Assign or revoke which client workspaces a member can access.",
            },
        ],
    },
    {
        id: "clients",
        label: "Clients",
        permissions: [
            {
                key: "clients.manage",
                label: "Manage clients",
                description: "See every client workspace and manage them, including creating new clients. Without this, the Clients page only shows workspaces the member belongs to.",
            },
        ],
    },
    {
        id: "slackBots",
        label: "Slack bots",
        permissions: [
            {
                key: "slackBots.manage",
                label: "Manage Slack bots",
                description: "Invite and remove Slack bot users.",
            },
        ],
    },
    {
        // One key per action on a creator's row in the NewForm One roster, so a
        // member can be trusted with outreach without being able to touch Meta
        // partnership state, or vice versa.
        id: "creators",
        label: "NewForm One creators",
        permissions: [
            {
                key: "creators.invite",
                label: "Invite and add creators",
                description: "Invite new creators to the program and add existing NewForm One creators to it.",
            },
            {
                key: "creators.sendLink",
                label: "Send portal links",
                description: "Send, resend, and copy a creator's portal link.",
            },
            {
                key: "creators.message",
                label: "Message creators",
                description: "Start conversations with creators on the program.",
            },
            {
                key: "creators.edit",
                label: "Edit creator details",
                description: "Change a creator's name and social handles. These are global to the creator, not per program.",
            },
            {
                key: "creators.photo",
                label: "Change creator photos",
                description: "Add or replace a creator's discovery photo.",
            },
            {
                key: "creators.remove",
                label: "Remove creators",
                description: "Remove a creator from the program.",
            },
            {
                key: "creators.metaPermission",
                label: "Manage Meta permissions",
                description: "Request, re-request, and refresh Instagram partnership-ads permissions.",
            },
            {
                key: "creators.tagging",
                label: "Manage tagging",
                description: "Allow or disallow creators to tag the brand in paid-partnership posts, and refresh approvals.",
            },
            {
                // A read, unlike the rest of the vocabulary: what NewForm has paid
                // creators is a narrower audience than who reviews their videos.
                // Granted on the member's own (NewForm) org, since the money is
                // NewForm's and the dashboard spans every program.
                key: "creators.payments",
                label: "View creator payments",
                description: "See the NewForm One payments dashboard: every transfer sent to a creator, with its videos, amounts and method.",
            },
        ],
    },
];
/**
 * Named grant packages — the starting points offered by the grants drawer
 * and usable by scripts (backfill, invite-with-template). One definition
 * so the drawer, backend, and tooling can never disagree about what
 * "Media buying" means.
 *
 * Semantics worth keeping true:
 * - "Media buying" holds batch.launch — the SPEND key. launchWithAssets
 *   (the endpoint that actually launches ads) and the scale-campaign
 *   launches (manualLaunch, manualLaunchAsync, autoLaunchNow) are gated on
 *   batch.launch.
 * - "Creative" deliberately has batch.create/edit WITHOUT batch.launch:
 *   they write briefs, drafts, and planned batches, but cannot spend.
 * - "Read only" is the empty set: view member orgs, write nothing.
 */
exports.PERMISSION_TEMPLATES = [
    {
        id: "media-buying",
        label: "Media buying",
        description: "Can launch, manage, and clean up tests and defaults.",
        keys: [
            "batch.create",
            "batch.edit",
            "batch.delete",
            "batch.launch",
            "monitoring.edit",
            "defaults.create",
            "defaults.edit",
            "defaults.delete",
        ],
    },
    {
        id: "creative",
        label: "Creative",
        description: "Can plan, create, and edit tests — but not launch them.",
        keys: ["batch.create", "batch.edit"],
    },
    {
        id: "full-access",
        label: "Full access",
        description: "Everything in this workspace.",
        keys: [...exports.PERMISSION_KEYS],
    },
    {
        id: "read-only",
        label: "Read only",
        description: "Can look at everything, can't change anything.",
        keys: [],
    },
];
